import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ─── ITEM 6: IST Timestamp Utilities ───────────────────────────
// All timestamps displayed in the system must be in IST (UTC+5:30)

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds

/**
 * Convert a UTC date string/Date to IST and format for display.
 * Full format: "30 Mar 2026, 11:10 PM IST"
 * @param {string|Date|null} dateInput - UTC timestamp (ISO string or Date object)
 * @param {object} options - { compact: boolean, dateOnly: boolean, timeOnly: boolean }
 * @returns {string} Formatted IST string
 */
export function formatIST(dateInput, options = {}) {
  if (!dateInput) return '-';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    
    // Convert to IST by adding 5:30 offset
    const istDate = new Date(date.getTime() + IST_OFFSET_MS);
    
    const day = istDate.getUTCDate();
    const dayPadded = day.toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNamesFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[istDate.getUTCMonth()];
    const monthFull = monthNamesFull[istDate.getUTCMonth()];
    const year = istDate.getUTCFullYear();
    
    let hours = istDate.getUTCHours();
    const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12
    const hourStr = hours.toString().padStart(2, '0');
    
    if (options.dateOnly) {
      return `${day} ${month} ${year}`;
    }
    
    // Format: "03 April 2026" (day padded, full month name)
    if (options.fullDate) {
      return `${dayPadded} ${monthFull} ${year}`;
    }
    
    if (options.timeOnly) {
      return `${hourStr}:${minutes} ${ampm} IST`;
    }
    
    if (options.compact) {
      return `${day} ${month}, ${hourStr}:${minutes} ${ampm}`;
    }
    
    return `${day} ${month} ${year}, ${hourStr}:${minutes} ${ampm} IST`;
  } catch (e) {
    return '-';
  }
}

/**
 * Format a date as relative time (e.g., "2 days ago", "3 hours ago")
 * @param {string|Date|null} dateInput
 * @returns {string}
 */
export function formatRelativeIST(dateInput) {
  if (!dateInput) return '-';
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatIST(dateInput, { compact: true });
  } catch (e) {
    return '-';
  }
}

/**
 * What to call an order in a list: the first item it carries, and how many more
 * ride with it.
 *
 * An order is one row but can be several products, and naming it after
 * `line_items[0]` alone said "Amla Carton" for a row that was also two other
 * things. The count is the honest part — the full list belongs in the title
 * attribute, which `itemNames` builds.
 *
 * @param {Array<{item_name?: string}>} items
 * @param {string} fallback  shown when nothing on the order is named yet
 */
export function itemsLabel(items, fallback = '—') {
  const names = (items || []).map(i => i?.item_name).filter(Boolean);
  if (!names.length) return fallback;
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
}

/** Every item on the order, one per line — for a tooltip. */
export function itemNames(items) {
  return (items || []).map(i => i?.item_name).filter(Boolean).join('\n');
}

/** The IST calendar day a timestamp falls on, as `YYYY-MM-DD`. */
export function istDayKey(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** "Today" / "Yesterday", else "12 Aug 2026". */
export function istDayLabel(dayKey) {
  if (!dayKey) return '';
  const today = istDayKey(new Date());
  const yesterday = istDayKey(new Date(Date.now() - 24 * 60 * 60 * 1000));
  if (dayKey === today) return 'Today';
  if (dayKey === yesterday) return 'Yesterday';
  return formatIST(`${dayKey}T06:00:00.000Z`, { dateOnly: true });
}
